import { Max, Min, NanQ, ToNumber } from "@pdfking/math"
import { Identity } from "@pdfking/tfunc"
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
} from "lucide-react"
import type { ComponentChildren } from "preact"

import { StepButton } from "./StepButton"
import { TooltipIconButton } from "./TooltipIconButton"

export function RangeControl({
  value,
  on_value,
  min = 0,
  max = 1,
  step = 1,
  input_class_name = "w-12",
  tooltip_side,
  start_description,
  previous_description,
  next_description,
  end_description,
  formatter = Identity,
  start_icon = <ChevronsLeftIcon />,
  decrement_icon = <ChevronLeftIcon />,
  increment_icon = <ChevronRightIcon />,
  end_icon = <ChevronsRightIcon />,
  show_input = true,
  children,
}: {
  value: number
  on_value: (n: number) => void
  min?: number
  max?: number
  step?: number
  input_class_name?: string
  // Jump-to-start/end and prev/next are each shown only when their
  // description is passed — callers that don't need a control just omit it.
  start_description?: string
  previous_description?: string
  next_description?: string
  end_description?: string
  tooltip_side?: "top" | "right" | "bottom" | "left"
  formatter?: (n: number) => string | number
  start_icon?: ComponentChildren
  decrement_icon?: ComponentChildren
  increment_icon?: ComponentChildren
  end_icon?: ComponentChildren
  // Slot between the input and the increment button — e.g. a jump-to-page
  // button. Callers that don't need one (ZoomControl) just omit it.
  children?: ComponentChildren
  show_input?: boolean
}) {
  return (
    <div className="flex items-center gap-1">
      {start_description && (
        <TooltipIconButton
          disabled={value <= min}
          onClick={() => on_value(min)}
          side={tooltip_side ?? "left"}
          title={start_description}
          variant="ghost"
        >
          {start_icon}
        </TooltipIconButton>
      )}
      {previous_description && (
        <StepButton
          direction="prev"
          max={max}
          min={min}
          on_value={on_value}
          side={tooltip_side ?? "left"}
          step={step}
          title={previous_description}
          value={value}
          variant="ghost"
        >
          {decrement_icon}
        </StepButton>
      )}
      {show_input && (
        <input
          className={`h-6 ${input_class_name} rounded bg-muted text-center text-sm outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`}
          // `onChange` (native `input`) fires per keystroke, so committing there
          // would send "1" then "3" as two separate pages instead of "13" —
          // commit only once typing is done (blur or Enter).
          onBlur={(e: Event) => {
            const target = e.target as HTMLInputElement
            // Committing a value clears the field back to empty — the number
            // just entered then shows as `value`'s new placeholder instead of
            // sitting in the field as text.
            if (!target.value) return
            const n = ToNumber(target.value)
            target.value = ""
            if (!NanQ(n)) on_value(Min(Max(min, n), max))
          }}
          onKeyDown={(e: KeyboardEvent) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur()
          }}
          max={max}
          min={min}
          placeholder={String(formatter(value))}
          step={step}
          type="number"
        />
      )}
      {children}
      {next_description && (
        <StepButton
          direction="next"
          max={max}
          min={min}
          on_value={on_value}
          side={tooltip_side ?? "right"}
          step={step}
          title={next_description}
          value={value}
          variant="ghost"
        >
          {increment_icon}
        </StepButton>
      )}
      {end_description && (
        <TooltipIconButton
          disabled={value >= max}
          onClick={() => on_value(max)}
          side={tooltip_side ?? "right"}
          title={end_description}
          variant="ghost"
        >
          {end_icon}
        </TooltipIconButton>
      )}
    </div>
  )
}
